/**
 * 睡眠エピソードの Garmin 生データ（開いたときに取得する）
 */

import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useSleepStore } from '@/store/useSleepStore'

import { flattenRaw, type RawRow } from '../raw-data'

/** 配列は数百件あるので、開いたときだけ JSON にする */
const ArrayValue = ({ items }: { items: unknown[] }) => {
  const [open, setOpen] = useState(false)
  if (items.length === 0) return <span className="text-muted-foreground">0 件</span>
  const Icon = open ? ChevronDown : ChevronRight
  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-0.5 text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon className="size-3" />
        {items.length} 件
      </button>
      {open && (
        <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted p-2 text-[10px] leading-tight">
          {JSON.stringify(items, null, 1)}
        </pre>
      )}
    </div>
  )
}

const RowValue = ({ row }: { row: RawRow }) => {
  if ('items' in row) return <ArrayValue items={row.items} />
  return (
    <>
      <span className="break-all">{row.value}</span>
      {row.time && <span className="block text-muted-foreground">{row.time}</span>}
    </>
  )
}

export const RawTable = ({ raw }: { raw: Record<string, unknown> }) => {
  const rows = useMemo(() => flattenRaw(raw), [raw])
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">データがありません</p>
  return (
    <table className="w-full table-fixed text-[11px]">
      <tbody>
        {rows.map((row) => (
          <tr key={row.path} className="border-b align-top last:border-0">
            <th className="w-1/2 py-1 pr-2 text-left font-normal break-all text-muted-foreground">{row.path}</th>
            <td className="py-1 tabular-nums">
              <RowValue row={row} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export const RawDataSection = ({ episodeId }: { episodeId: number }) => {
  const state = useSleepStore((s) => s.episodeRaws.get(episodeId))
  const fetchEpisodeRaw = useSleepStore((s) => s.fetchEpisodeRaw)

  return (
    <details
      onToggle={(e) => {
        if (e.currentTarget.open) void fetchEpisodeRaw(episodeId)
      }}
    >
      <summary className="cursor-pointer text-xs text-muted-foreground">Garmin の生データ</summary>
      <div className="mt-2">
        {state?.status === 'loading' && <p className="text-xs text-muted-foreground">読み込み中…</p>}
        {state?.status === 'error' && <p className="text-xs text-rose-600">{state.message}</p>}
        {state?.status === 'done' && <RawTable raw={state.raw} />}
      </div>
    </details>
  )
}
